/**
 * TankLabelsRenderer — world-space per-player elements that track tank positions.
 *
 * Owns: damage/HP texts, power bars, grapple ammo texts, name labels,
 * heal flash timers, damage flash timers, prev damage/HP tracking.
 *
 * Extracted from HUDRenderer as a composition class.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { HUD, HUD_LAYOUT, PLAYER_COLORS, Z, CLASSIC, BRACE } from '../../constants';
import type { GameState } from '../../types';

export class TankLabelsRenderer {
  private worldContainer: Container;

  private damageTexts: Text[];
  private powerBars: Graphics[];
  private grappleAmmoTexts: Text[];
  private nameLabels: Text[];

  private healFlashTimers: number[];
  private damageFlashTimers: number[];
  private prevDamagePercent: number[];
  private prevHp: number[];

  constructor(worldContainer: Container, playerCount: number) {
    this.worldContainer = worldContainer;
    this.damageTexts = [];
    this.powerBars = [];
    this.grappleAmmoTexts = [];
    this.nameLabels = [];
    this.healFlashTimers = new Array(playerCount).fill(0);
    this.damageFlashTimers = new Array(playerCount).fill(0);
    this.prevDamagePercent = new Array(playerCount).fill(0);
    this.prevHp = new Array(playerCount).fill(CLASSIC.MAX_HP);

    for (let i = 0; i < playerCount; i++) {
      const dmgText = new Text({
        text: '0%',
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: HUD.DAMAGE_FONT_SIZE,
          fontWeight: 'bold',
          fill: HUD.DAMAGE_COLOR_LOW,
          align: 'center',
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      dmgText.anchor.set(0.5, 0.5);
      dmgText.zIndex = Z.TANKS + 2;
      worldContainer.addChild(dmgText);
      this.damageTexts.push(dmgText);

      const pBar = new Graphics();
      pBar.zIndex = Z.TANKS + 2;
      worldContainer.addChild(pBar);
      this.powerBars.push(pBar);

      const grappleText = new Text({
        text: '',
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 11,
          fontWeight: 'bold',
          fill: 0x88aaff,
          align: 'center',
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      grappleText.anchor.set(0.5, 0.5);
      grappleText.zIndex = Z.TANKS + 2;
      grappleText.visible = false;
      worldContainer.addChild(grappleText);
      this.grappleAmmoTexts.push(grappleText);

      const nameLabel = new Text({
        text: `P${i + 1}`,
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: HUD_LAYOUT.NAME_LABEL_FONT_SIZE,
          fill: PLAYER_COLORS[i],
          align: 'center',
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      nameLabel.anchor.set(0.5, 1);
      nameLabel.zIndex = Z.TANKS + 2;
      worldContainer.addChild(nameLabel);
      this.nameLabels.push(nameLabel);
    }
  }

  triggerHealFlash(playerIndex: number): void {
    if (playerIndex >= 0 && playerIndex < this.healFlashTimers.length) {
      this.healFlashTimers[playerIndex] = BRACE.HEAL_FLASH_DURATION;
    }
  }

  hide(): void {
    for (const dt of this.damageTexts) dt.visible = false;
    for (const pb of this.powerBars) pb.visible = false;
    for (const ga of this.grappleAmmoTexts) ga.visible = false;
    for (const nl of this.nameLabels) nl.visible = false;
  }

  update(state: GameState, dt: number): void {
    // Cap loops to created element count (roguelite dynamically adds players beyond initial count)
    const maxLabeled = this.damageTexts.length;

    // --- Damage / HP text (world-space) — hidden, cards show damage now ---
    for (let i = 0; i < maxLabeled; i++) {
      this.damageTexts[i].visible = false;
    }

    // --- Power bars (world-space, only visible when charging) ---
    for (let i = 0; i < Math.min(state.players.length, maxLabeled); i++) {
      const p = state.players[i];
      const pBar = this.powerBars[i];
      pBar.clear();

      if (!p.alive || !p.charging) {
        pBar.visible = false;
        continue;
      }
      pBar.visible = true;

      const barX = p.x - HUD.POWER_BAR_WIDTH / 2;
      const barY = p.y + HUD.POWER_BAR_OFFSET_Y;

      pBar.rect(barX - 1, barY - 1, HUD.POWER_BAR_WIDTH + 2, HUD.POWER_BAR_HEIGHT + 2);
      pBar.fill({ color: HUD.BAR_BORDER_COLOR });

      pBar.rect(barX, barY, HUD.POWER_BAR_WIDTH, HUD.POWER_BAR_HEIGHT);
      pBar.fill({ color: 0x111111 });

      const fillColor = p.power > 0.75 ? HUD.POWER_COLOR_HIGH : HUD.POWER_COLOR_NORMAL;
      const fillWidth = HUD.POWER_BAR_WIDTH * p.power;
      if (fillWidth > 0) {
        pBar.rect(barX, barY, fillWidth, HUD.POWER_BAR_HEIGHT);
        pBar.fill({ color: fillColor });
      }
    }

    // --- Grapple ammo (currently disabled) ---
    for (const gText of this.grappleAmmoTexts) {
      gText.visible = false;
    }

    // --- Tank name labels (world-space, below tank) ---
    for (let i = 0; i < Math.min(state.players.length, maxLabeled); i++) {
      const p = state.players[i];
      const label = this.nameLabels[i];
      label.visible = p.alive;
      if (p.alive) {
        if (p.tankId === 'terraformer') {
          const modeTag = p.terraformerMode === 'sapper' ? ' [SAP]' : ' [MAS]';
          label.text = p.name + modeTag;
        } else {
          label.text = p.name;
        }
        label.position.set(p.x, p.y + HUD_LAYOUT.NAME_LABEL_OFFSET_Y);
      }
    }
  }

  destroy(): void {
    for (const t of this.damageTexts) t.destroy();
    for (const b of this.powerBars) b.destroy();
    for (const g of this.grappleAmmoTexts) g.destroy();
    for (const l of this.nameLabels) l.destroy();
  }
}
